/* eslint-disable no-console */
/* eslint-disable no-constant-condition */
import {put, call, select, fork, join} from 'redux-saga/effects';

import {
    fetchInitialLive,
    fetchLiveUpdate,
    fetchInitialTimetable,
    fetchTimetableUpdate,
    fetchUserBalance,
    fetchFreebet,
    fetchBetStatus,
    fetchStakeStatus,
    fetchCashoutStatus
} from './api';

import {sagaActions} from './actions';

import {
    selectForLive,
    selectForTimetable,
    selectUser,
    selectForBets,
    selectForStakes,
    selectForCashout
} from './selectors';

const {
    live_initial,
    live_update,
    timetable_initial,
    timetable_update,
    get_balance,
    get_freebet,
    get_bet_status,
    get_stake_status,
    get_cashout_status
} = sagaActions;

import ModelKeyRepository from '../repositories/ImmutableRepository';
import KeyModifier from '../repositories/modifier/ExpandKeyModifier';

import * as RepositoryState from 'constants/RepositoryState';

const liveRepository = new ModelKeyRepository();
const timetableRepository = new ModelKeyRepository();

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

function transformData(data, section) {
    let repo;

    switch (section) {
        case 'live':
            repo = liveRepository;
            break;
        case 'timetable':
            repo = timetableRepository;
            break;
    }

    if (!repo) {
        return data;
    }

    const modifier = new KeyModifier(repo);

    if (modifier) {
        modifier.modify(data);
    }

    return data;
}

function *fetchActionWithTransform(action, apiFn, section, requestData) {
    try {
        yield put(action.request({...requestData}));
        const {version, content, expired, error} = yield call(apiFn, requestData);
        if (content) {
            const transformed = transformData(content, section);
            const data = {
                expired,
                version,
                ...transformed
            };
            yield put(action.success(data));
        } else {
            yield put(action.failure(requestData, error));
        }
    } catch (error) {
        yield put(action.failure(requestData, error));
    }
}

function *fetchAction(action, apiFn, requestData) {
    yield put(action.request(requestData));
    const {response, error} = yield call(apiFn, requestData);

    if (response) {
        yield put(action.success(response));
    } else {
        yield put(action.failure(requestData, error));
    }
}

const fetchInitialLiveSaga = fetchActionWithTransform.bind(null, live_initial, fetchInitialLive, 'live');
const fetchLiveUpdateSaga = fetchActionWithTransform.bind(null, live_update, fetchLiveUpdate, 'live');

const fetchInitialTimetableSaga = fetchActionWithTransform.bind(null, timetable_initial, fetchInitialTimetable, 'timetable');
const fetchTimetableUpdateSaga = fetchActionWithTransform.bind(null, timetable_update, fetchTimetableUpdate, 'timetable');

const fetchBalanceSaga = fetchAction.bind(null, get_balance, fetchUserBalance);
const fetchFreebetSaga = fetchAction.bind(null, get_freebet, fetchFreebet);

const fetchBetsStatusSaga = fetchAction.bind(null, get_bet_status, fetchBetStatus);
const fetchStakeStatusSaga = fetchAction.bind(null, get_stake_status, fetchStakeStatus);
const fetchCashoutsStatusSaga = fetchAction.bind(null, get_cashout_status, fetchCashoutStatus);

export function *loadInitialLive() {
    while (true) {
        const requestData = yield select(selectForLive);
        const path = requestData.path;
        const eligible = requestData.repoState === RepositoryState.NOT_INITED &&
            (path === '/live' || path === '/live/timetable');
        if (eligible) {
            yield call(fetchInitialLiveSaga, requestData);
        }

        yield call(delay, 2000);
    }
}

export function *loadLiveUpdate() {
    while (true) {
        const requestData = yield select(selectForLive);
        const repoState = requestData.repoState;
        const path = requestData.path;

        const eligible = (repoState === RepositoryState.INITED || repoState === RepositoryState.UPDATING) &&
            (path === '/live' || path === '/live/timetable');
        if (eligible) {
            try{
                yield call(fetchLiveUpdateSaga, requestData);
            }catch(error){
                console.error('(/var/www/site/js/sagas/index.js): ', error);
            }
        }

        yield call(delay, 2000);
    }
}

export function *loadInitialTimetable() {
    while (true) {
        const requestData = yield select(selectForTimetable);
        const path = requestData.path;

        const eligible = requestData.repoState === RepositoryState.NOT_INITED &&
            (path === '/live' || path === '/live/timetable');
        if (eligible) {
            yield call(fetchInitialTimetableSaga, requestData);
        }

        yield call(delay, 3000);
    }
}

export function *loadTimetableUpdate() {
    while (true) {
        const requestData = yield select(selectForTimetable);
        const repoState = requestData.repoState;
        const path = requestData.path;

        const eligible = (repoState === RepositoryState.INITED || repoState === RepositoryState.UPDATING) &&
            (path === '/live' || path === '/live/timetable');
        if (eligible) {
            yield call(fetchTimetableUpdateSaga, requestData);
            yield call(delay, 30000);
        } else {
            yield call(delay, 3000);
        }
    }
}

export function *loadBalance() {
    while (true) {
        const selectData = yield select(selectUser);
        const user = selectData.user;
        if (user) {
            const data = {accountId: user.get('accountId')};
            yield call(fetchBalanceSaga, data);
        }
        
        yield call(delay, 10000);
    }
}

export function *loadFreebet() {
    while (true) {
        const selectData = yield select(selectUser);
        if (selectData.user) {
            yield call(fetchFreebetSaga);
            yield call(delay, 5 * 60 * 1000);
        } else {
            yield call(delay, 60 * 1000);
        }
    }
}

export function *loadBetsStatus() {
    while (true) {
        const requestData = yield select(selectForBets);
        const {languageId, bets} = requestData;
        if (bets.length > 0) {
            const tasks = yield bets.map(b =>
                fork(fetchBetsStatusSaga, {languageId, betId: b.betId, stakeId: b.stakeId, delay: b.delay}));
            yield tasks.map(join);
            yield call(delay, 1000);
        } else {
            yield call(delay, 1500);
        }
    }
}

export function *loadStakesStatus() {
    while (true) {
        const requestData = yield select(selectForStakes);
        const bets = requestData.bets;

        if (bets.length > 0) {
            const tasks = yield bets.map(b => fork(fetchStakeStatusSaga, {betId: b.betId, stakeId: b.stakeId}));
            yield tasks.map(join);

            yield call(delay, 30000);
        } else {
            yield call(delay, 5000);
        }
    }
}

export function *loadCashoutsStatus() {
    while (true) {
        const requestData = yield select(selectForCashout);
        const bets = requestData.bets;

        if (bets.length > 0) {
            const tasks = yield bets.map(b => fork(fetchCashoutsStatusSaga, {betId: b.betId, requestId: b.requestId}));
            yield tasks.map(join);

            yield call(delay, 1000);
        } else {
            yield call(delay, 3000);
        }
    }
}



// WEBPACK FOOTER //
// ./js/sagas/index.js